/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Brick, registerBrick, Workflow, WorkflowObjectState, Order, Predicate } from 'olympe';
import {combineLatest} from "rxjs";
import {getLogger} from "@olympeio/core";

export default class WorkflowIsObjectInState extends Brick {

    /**
     * @override
     */
    setupExecution($) {
        // Propagate null values if an input is cleared
        return combineLatest(this.getInputs().map(i => $.observe(i)));
    }

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!CloudObject} object
     * @param {!WorkflowState} state
     * @param {function(boolean)} setValue
     */
    update($, [object, state], [setValue]) {
        if (object === null || state === null) {
            getLogger('Is object in state').error('No object or state received');
            setValue(false);
        } else {
            // Check if the latest WorkflowObjectState and check if it's the same as `state`
            const workflow = state.followSingle(Workflow.statesRel.getInverse()).executeFromCache();
            object.follow(WorkflowObjectState.objectRel.getInverse())
                .sortBy(WorkflowObjectState.dateTimeProp, Order.DESC)
                .filter(Predicate.equals(WorkflowObjectState.workflowProp, workflow.name()))
                .limit(1)
                .observe($)
                .subscribe(queryResult => {
                    const objectState = queryResult.getFirst();
                    setValue(objectState && state.name() === objectState.get(WorkflowObjectState.stateProp));
                });
        }
    }
}

registerBrick('0180dc7f93c936dad83f', WorkflowIsObjectInState);
